import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  InputNumber,
  Row,
  Col,
  Divider,
  DatePicker,
  Checkbox,
} from "antd";
import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const EducationModal = ({
  visible,
  onCancel,
  onSave,
  completionStatuses,
  educationLanguages,
  educationTypes,
  schoolTypes,
  initialValues,
  onEdit,
  isSubmitting,
}) => {
  const [form] = Form.useForm();
  const [isOngoing, setIsOngoing] = useState(false);
  const [selectedSchoolType, setSelectedSchoolType] = useState(
    initialValues?.type || null
  );

  const isEditMode = initialValues?.data !== null;

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (isEditMode) {
        await onEdit(values, initialValues.data.id);
      } else {
        await onSave(values);
      }
      form.resetFields();
      setSelectedSchoolType(null);
    } catch (error) {
      console.error("Form validation or submission failed:", error);
    }
  };

  useEffect(() => {
    if (!isEditMode) {
      form.resetFields();
      return;
    }

    const isOngoing =
      !initialValues.data.endMonth || !initialValues.data.endYear;
    setIsOngoing(isOngoing);
    setSelectedSchoolType(initialValues.type);

    const baseFields = {
      schoolType: initialValues.data.schoolType?.name,

      schoolName: initialValues.data.schoolName,
      completionStatus: initialValues.data.completionStatus?.name,
      startDate: dayjs(
        `${initialValues.data.startYear}-${initialValues.data.startMonth}`
      ),
      endDate: isOngoing
        ? null
        : dayjs(`${initialValues.data.endYear}-${initialValues.data.endMonth}`),
      isOngoing: isOngoing,
    };

    const additionalFields = {
      HIGH_SCHOOL: {
        educationLanguageId: initialValues.data.educationLanguage?.id,
        description: initialValues.data.description,
      },
      UNIVERSITY: {
        educationType: initialValues.data.educationType?.name,
        educationLanguageId: initialValues.data.educationLanguage?.id,
        faculty: initialValues.data.faculty,
        department: initialValues.data.department,
        gpa: initialValues.data.gpa,
        description: initialValues.data.description,
      },
    };

    form.setFieldsValue({
      ...baseFields,
      ...additionalFields[initialValues.type],
    });
  }, [initialValues, isEditMode, form]);

  const renderBasicFields = () => (
    <>
      <Form.Item
        name="schoolName"
        label="Okul Adı"
        rules={[{ required: true, message: "Lütfen okul adını giriniz" }]}>
        <Input placeholder="Okul adını giriniz" />
      </Form.Item>

      <Row gutter={16} justify="space-between">
        <Col xs={24} md={12}>
          <Form.Item
            label="Başlangıç Tarihi"
            name="startDate"
            rules={[
              { required: true, message: "Lütfen başlangıç tarihi seçiniz" },
            ]}>
            <DatePicker style={{ width: "100%" }} picker="month" />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item label="Bitiş Tarihi" name="endDate">
            <DatePicker
              style={{ width: "100%" }}
              picker="month"
              disabled={isOngoing}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="isOngoing" valuePropName="checked">
        <Checkbox onChange={(e) => setIsOngoing(e.target.checked)}>
          Eğitimim Devam Ediyor
        </Checkbox>
      </Form.Item>

      <Form.Item
        name="completionStatus"
        label="Mezuniyet Durumu"
        rules={[
          {
            required: !isOngoing,
            message: "Lütfen mezuniyet durumu seçiniz",
          },
        ]}>
        <Select
          options={completionStatuses}
          placeholder="Mezuniyet durumu seçiniz"
          disabled={isOngoing}
        />
      </Form.Item>
    </>
  );

  const renderHighSchoolFields = () => (
    <>
      <Form.Item
        name="educationLanguageId"
        label="Öğrenim Dili"
        rules={[{ required: true, message: "Lütfen öğrenim dilini seçiniz" }]}>
        <Select
          options={educationLanguages}
          placeholder="Öğrenim Dili Seçiniz"
        />
      </Form.Item>

      <Form.Item name="description" label="Ek Bilgiler">
        <Input.TextArea rows={4} />
      </Form.Item>
    </>
  );

  const renderUniversityFields = () => (
    <>
      <Row gutter={16}>
        {/* <Col span={12}>
          <Form.Item
            name="schoolType"
            label="Eğitim Seviyesi"
            rules={[
              { required: true, message: "Lütfen eğitim seviyesini seçiniz" },
            ]}>
            <Select options={schoolTypes?.slice(2)} />
          </Form.Item>
        </Col> */}
        <Col span={12}>
          <Form.Item
            name="educationType"
            label="Öğrenim Türü"
            rules={[
              { required: true, message: "Lütfen öğrenim türünü seçiniz" },
            ]}>
            <Select
              options={educationTypes}
              placeholder="Öğrenim Türü Seçiniz"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="faculty"
            label="Fakülte"
            rules={[
              { required: true, message: "Lütfen fakülte bilgisi giriniz" },
            ]}>
            <Input placeholder="Fakülte Giriniz" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="department"
            label="Bölüm"
            rules={[
              { required: true, message: "Lütfen bölüm bilgisi giriniz" },
            ]}>
            <Input placeholder="Bölüm Giriniz" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="educationLanguageId"
            label="Öğrenim Dili"
            rules={[
              { required: true, message: "Lütfen öğrenim dilini seçiniz" },
            ]}>
            <Select
              options={educationLanguages}
              placeholder="Öğrenim Dili Seçiniz"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="gpa" label="Not Ortalaması">
            <InputNumber
              min={0}
              max={4}
              step={0.01}
              placeholder="0.00 - 4.00"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="description" label="Ek Bilgiler">
        <Input.TextArea rows={4} />
      </Form.Item>
    </>
  );

  const handleSchoolTypeChange = (value) => {
    setSelectedSchoolType(value);
    form.resetFields();
    form.setFieldValue("schoolType", value);
  };

  console.log("is edit mode ", isEditMode);

  return (
    <Modal
      open={visible}
      onCancel={() => {
        onCancel();
        setSelectedSchoolType(null);
        form.resetFields();
      }}
      footer={null}
      closeIcon={<CloseOutlined />}
      width={800}
      title="Eğitim Bilgileri">
      <Form form={form} layout="vertical">
        <Form.Item
          name="schoolType"
          label="Eğitim Tipi"
          rules={[{ required: true, message: "Lütfen eğitim tipini seçiniz" }]}>
          <Select
            disabled={isEditMode}
            placeholder="Eğitim tipini seçiniz"
            onChange={handleSchoolTypeChange}
            options={schoolTypes?.map((type) => ({
              value: type.value,
              label: type.label,
            }))}
          />
        </Form.Item>

        {(selectedSchoolType || isEditMode) && (
          <>
            {renderBasicFields()}
            {selectedSchoolType === "HIGH_SCHOOL" && renderHighSchoolFields()}
            {(selectedSchoolType === "UNIVERSITY" ||
              form.getFieldValue("schoolType") === "BACHELORS_DEGREE" ||
              form.getFieldValue("schoolType") === "MASTER" ||
              form.getFieldValue("schoolType") === "PHD") &&
              renderUniversityFields()}

            <Divider />

            <Form.Item>
              <Button
                type="primary"
                onClick={handleSave}
                loading={isSubmitting}
                icon={<SaveOutlined />}
                block>
                Kaydet
              </Button>
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EducationModal;
